import classnames from 'classnames';
import Image from 'next/image';
import React from 'react';
import { useInView } from 'react-intersection-observer';

export type ParallaxLayoutListItem<T> = {
  title: string;
  data: T;
};

type ParallaxLayoutListProps<T> = {
  items: ParallaxLayoutListItem<T>[];
  renderItem: (data: T) => React.ReactNode;
};
function ParallaxLayoutList<T>(props: ParallaxLayoutListProps<T>) {
  const { items, renderItem } = props;
  return (
    <div className="inline-block my-10">
      {items.map((item, index) => (
        <ParallaxLayoutRow key={`parallax-layout-list-item-${index}`} title={item.title}>
          {renderItem(item.data)}
        </ParallaxLayoutRow>
      ))}
    </div>
  );
}
ParallaxLayoutList.displayName = 'ParallaxLayoutList';
export default ParallaxLayoutList;

/************************************************************************************************
 * Helpers
 ************************************************************************************************/

type ParallaxLayoutRowProps<T> = {
  title: string;
  children?: React.ReactNode;
};
function ParallaxLayoutRow<T>(props: ParallaxLayoutRowProps<T>) {
  const { title, children } = props;
  const { ref: containerRef, inView } = useInView({ triggerOnce: false });
  return (
    <div>
      <h4
        className={classnames('font-bold flex gap-2 items-center mb-4 md:mb-0', {
          'relative ': !inView,
          'md:sticky md:top-[200px]': inView,
        })}
      >
        <div className={classnames('inline-block rounded-full bg-purple min-w-[15px] min-h-[15px] md:min-w-[20px] md:min-h-[20px]')} />
        {title}
      </h4>
      <div ref={containerRef} className="w-full flex flex-col items-end">
        <div className="w-full md:w-[33.33%] mb-4 md:mb-0">{children}</div>
      </div>
    </div>
  );
}
ParallaxLayoutRow.displayName = 'ParallaxLayoutRow';

type ParallaxLayoutTextListCardProps = {
  description: string;
  entries: string[];
};
export const ParallaxLayoutTextListCard: React.FC<ParallaxLayoutTextListCardProps> = React.memo((props) => {
  const { description, entries } = props;
  return (
    <div className="sm:mt-[20px] sm:mb-[40px] md:mt-[60px] md:mb-[160px]">
      <p className="text-2xl mb-4 font-bold">{description}</p>
      <ul className="list-disc ml-4 text-lg">
        {entries.map((entry, index) => (
          <li className="mb-4" key={index}>
            {entry}
          </li>
        ))}
      </ul>
    </div>
  );
});
ParallaxLayoutTextListCard.displayName = 'ParallaxLayoutTextListCard';

type ParallaxLayoutTextDecsriptionCardProps = {
  description: string;
};
export const ParallaxLayoutTextDecsriptionCard: React.FC<ParallaxLayoutTextDecsriptionCardProps> = React.memo((props) => {
  const { description } = props;
  return (
    <div className="sm:mt-[20px] sm:mb-[40px] md:mt-[60px] md:mb-[160px]">
      <p className="text-xl mb-4">{description}</p>
    </div>
  );
});
ParallaxLayoutTextDecsriptionCard.displayName = 'ParallaxLayoutTextDecsriptionCard';

type ParallaxLayoutTextImageCardProps = {
  description: string;
  imageUrl: string;
  paragraphs?: string[];
};
export const ParallaxLayoutTextImageCard: React.FC<ParallaxLayoutTextImageCardProps> = React.memo((props) => {
  const { description, imageUrl, paragraphs } = props;
  return (
    <div className="sm:mt-[20px] sm:mb-[40px] md:mt-[60px] md:mb-[160px]">
      <Image width={400} height={100} src={imageUrl} alt={description} className="rounded-xl mb-6" />
      <p className="text-2xl mb-4 font-bold">{description}</p>
      {(paragraphs || []).map((entry, index) => (
        <p className="mb-4" key={index}>
          {entry}
        </p>
      ))}
    </div>
  );
});
ParallaxLayoutTextImageCard.displayName = 'ParallaxLayoutTextImageCard';
